import React from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '../contexts/ThemeContext';
import { TreeStructure, TreeBranch } from '../types/DecisionTree';
import { DecisionTreeData } from '../types/DecisionTree';
import { getPreventionScoreColor, getPreventionScoreBarColor, getPreventionScoreRating } from '../services/PreventionScoringService';
import { getPathComparison } from '../services/DecisionTreeService';

interface DecisionTreeVisualizationProps {
  treeStructure: TreeStructure;
  treeData: DecisionTreeData;
}

export const DecisionTreeVisualization: React.FC<DecisionTreeVisualizationProps> = ({
  treeStructure,
  treeData
}) => {
  const { isLight } = useTheme();
  const { branches, playerSelections } = treeStructure;
  const comparison = getPathComparison(treeData);

  if (branches.length === 0) {
    return (
      <div className={`p-6 text-center ${isLight ? 'text-slate-500' : 'text-slate-400'}`}>
        No decision data available.
      </div>
    );
  }

  return (
    <div className={`rounded-xl p-6 ${
      isLight
        ? 'bg-white border border-slate-200 shadow-lg'
        : 'bg-white/5 border border-white/10 backdrop-blur-md'
    }`}>
      {/* Header with comparison stats */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-6 gap-4">
        <div>
          <h3 className={`text-lg font-semibold ${isLight ? 'text-slate-800' : 'text-slate-100'}`}>
            Decision Tree Analysis
          </h3>
          <p className={`text-sm ${isLight ? 'text-slate-500' : 'text-slate-400'}`}>
            Compare your path against {treeData.totalPaths} possible outcomes
          </p>
        </div>

        {/* Stats */}
        <div className="flex gap-4 flex-wrap">
          <div className={`px-4 py-2 rounded-lg ${
            isLight ? 'bg-slate-100' : 'bg-white/5'
          }`}>
            <div className={`text-xs ${isLight ? 'text-slate-500' : 'text-slate-400'}`}>Your Score</div>
            <div className={`text-lg font-bold ${getPreventionScoreColor(comparison.playerScore, isLight)}`}>
              {comparison.playerScore}
            </div>
          </div>
          <div className={`px-4 py-2 rounded-lg ${
            isLight ? 'bg-green-50' : 'bg-green-500/10'
          }`}>
            <div className={`text-xs ${isLight ? 'text-green-600' : 'text-green-400'}`}>Best Possible</div>
            <div className={`text-lg font-bold ${isLight ? 'text-green-600' : 'text-green-400'}`}>
              {comparison.bestScore}
            </div>
          </div>
        </div>
      </div>

      {/* Tree Visualization */}
      <div className="overflow-hidden">
        <div>
          {branches.map((branch, branchIndex) => (
            <BranchRow
              key={branch.incident.id}
              branch={branch}
              branchIndex={branchIndex}
              isLast={branchIndex === branches.length - 1}
              playerSelection={playerSelections.get(branch.incident.id)}
              isLight={isLight}
            />
          ))}
        </div>
      </div>

      {/* Legend */}
      <div className={`mt-6 pt-4 border-t ${isLight ? 'border-slate-200' : 'border-white/10'}`}>
        <div className="flex flex-wrap gap-4 justify-center text-xs">
          <div className="flex items-center gap-2">
            <div className={`w-4 h-4 rounded ${
              isLight ? 'bg-professional-blue-500' : 'bg-professional-blue-500'
            }`} />
            <span className={isLight ? 'text-slate-600' : 'text-slate-400'}>Your Choice</span>
          </div>
          <div className="flex items-center gap-2">
            <div className={`w-4 h-4 rounded border-2 ${
              isLight ? 'border-slate-300' : 'border-white/30'
            }`} />
            <span className={isLight ? 'text-slate-600' : 'text-slate-400'}>Alternative</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-green-500" />
            <span className={isLight ? 'text-slate-600' : 'text-slate-400'}>Good (61-100)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-yellow-500" />
            <span className={isLight ? 'text-slate-600' : 'text-slate-400'}>Moderate (31-60)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-red-500" />
            <span className={isLight ? 'text-slate-600' : 'text-slate-400'}>Poor (0-30)</span>
          </div>
        </div>
      </div>
    </div>
  );
};

interface BranchRowProps {
  branch: TreeBranch;
  branchIndex: number;
  isLast: boolean;
  playerSelection?: string;
  isLight: boolean;
}

const BranchRow: React.FC<BranchRowProps> = ({
  branch,
  branchIndex,
  isLast,
  playerSelection,
  isLight
}) => {
  const { incident, options } = branch;

  return (
    <motion.div
      className={`flex items-stretch ${!isLast ? 'mb-4' : ''}`}
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: 0.1 * branchIndex, duration: 0.3 }}
    >
      {/* Incident Label */}
      <div className={`w-64 flex-shrink-0 pr-4 py-2 ${
        isLight ? 'border-r border-slate-200' : 'border-r border-white/10'
      }`}>
        <div className="flex items-center gap-2 mb-2">
          <span className={`text-xs font-medium px-2 py-1 rounded ${
            isLight ? 'bg-slate-100 text-slate-600' : 'bg-white/10 text-slate-400'
          }`}>
            #{branchIndex + 1}
          </span>
          <span className={`text-xs px-2 py-1 rounded uppercase font-medium ${
            incident.severity === 'critical'
              ? isLight ? 'bg-red-100 text-red-700' : 'bg-red-500/20 text-red-400'
              : incident.severity === 'high'
              ? isLight ? 'bg-orange-100 text-orange-700' : 'bg-orange-500/20 text-orange-400'
              : incident.severity === 'medium'
              ? isLight ? 'bg-yellow-100 text-yellow-700' : 'bg-yellow-500/20 text-yellow-400'
              : isLight ? 'bg-green-100 text-green-700' : 'bg-green-500/20 text-green-400'
          }`}>
            {incident.severity}
          </span>
        </div>
        <h4 className={`text-sm font-medium ${
          isLight ? 'text-slate-800' : 'text-slate-200'
        }`}>
          {incident.title}
        </h4>
      </div>

      {/* Options */}
      <div className="flex-1 pl-4 flex gap-3 flex-wrap py-2">
        {options.map((opt, optIndex) => {
          const isSelected = playerSelection === opt.option.id;
          const scoreColor = getPreventionScoreColor(opt.overallScore, isLight);
          const barColor = getPreventionScoreBarColor(opt.overallScore);

          return (
            <motion.div
              key={opt.option.id}
              className={`relative flex-1 min-w-[200px] max-w-[300px] p-4 rounded-lg transition-all duration-200 ${
                isSelected
                  ? isLight
                    ? 'bg-professional-blue-50 border-2 border-professional-blue-500 shadow-md'
                    : 'bg-professional-blue-500/20 border-2 border-professional-blue-500'
                  : isLight
                  ? 'bg-slate-50 border-2 border-slate-200'
                  : 'bg-white/5 border-2 border-white/10'
              }`}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.1 * branchIndex + 0.05 * optIndex }}
              whileHover={{ scale: 1.02 }}
            >
              {/* Selected badge */}
              {isSelected && (
                <div className={`absolute -top-2 -right-2 px-2 py-0.5 text-[10px] font-bold rounded ${
                  isLight
                    ? 'bg-professional-blue-500 text-white'
                    : 'bg-professional-blue-500 text-white'
                }`}>
                  YOUR CHOICE
                </div>
              )}

              {/* Option Label */}
              <div className={`text-sm font-medium mb-3 ${
                isSelected
                  ? isLight ? 'text-professional-blue-700' : 'text-professional-blue-300'
                  : isLight ? 'text-slate-700' : 'text-slate-200'
              }`}>
                {opt.option.label}
              </div>

              {/* Score */}
              <div className="flex items-center justify-between">
                <span className={`text-lg font-bold ${scoreColor}`}>
                  {opt.overallScore}
                </span>
                <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                  opt.overallScore >= 61
                    ? isLight ? 'bg-green-100 text-green-700' : 'bg-green-500/20 text-green-400'
                    : opt.overallScore >= 31
                    ? isLight ? 'bg-yellow-100 text-yellow-700' : 'bg-yellow-500/20 text-yellow-400'
                    : isLight ? 'bg-red-100 text-red-700' : 'bg-red-500/20 text-red-400'
                }`}>
                  {getPreventionScoreRating(opt.overallScore)}
                </span>
              </div>

              {/* Score bar */}
              <div className={`mt-2 h-1.5 rounded-full overflow-hidden ${
                isLight ? 'bg-slate-200' : 'bg-white/10'
              }`}>
                <motion.div
                  className={`h-full rounded-full ${barColor}`}
                  initial={{ width: 0 }}
                  animate={{ width: `${opt.overallScore}%` }}
                  transition={{ delay: 0.2 * branchIndex + 0.1 * optIndex, duration: 0.5 }}
                />
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Connector line to next row */}
      {!isLast && (
        <div className={`absolute left-48 ml-4 w-px h-4 -bottom-4 ${
          isLight ? 'bg-slate-200' : 'bg-white/10'
        }`} />
      )}
    </motion.div>
  );
};

export default DecisionTreeVisualization;
